import { groupBy, orderBy } from 'lodash-es'

import { Icon } from '@fv/client-components'
import type { TrackEvent } from '@fv/client-types'
import type { InterlineDetails, TrackDateTime } from '@fv/models'
import { getTrackEventDate } from '@fv/models/core'

import { List } from '@/components/List'
import { Loading } from '@/components/Loading'

import { DeliveryAppointment } from './DeliveryDetailComponents'
import { useTracking } from './queries'
import {
  InterlineListItem,
  StopsAwayListItem,
  TrackEventListItem,
} from './TrackEventListItem'
import { timeZoneAbbreviation } from './utils'

type Props = {
  loadId: string
}
export const DetailedTrackingHistory = (props: Props) => {
  const { data, isLoading } = useTracking(props.loadId)

  const eventsByDays = Object.entries(
    groupBy(data?.trackEvents, event => {
      return getTrackEventDate(event.eventDateTime).format('YYYY-MM-DD')
    }),
  )
  const orderedDays = orderBy(eventsByDays, ([date]) => date, ['desc'])

  if (isLoading) {
    return <Loading />
  }
  return (
    <div>
      <DeliveryAppointment track={data} />
      {orderedDays.map(([date, events], ix) => (
        <TrackingDay
          key={date}
          stopsAway={ix === 0 ? data.deliveryDetails?.stopsAway : undefined}
          interline={ix === 0 ? data.interlineDetails : undefined}
          date={events[0].eventDateTime}
          hasAmbiguousTimes={events.some(e => !e.eventDateTime.isUTC)}
          events={events}
        />
      ))}
    </div>
  )
}

type DayProps = {
  date: TrackDateTime
  events: Array<TrackEvent>
  stopsAway?: number
  interline?: InterlineDetails
  hasAmbiguousTimes: boolean
}
const TrackingDay = (props: DayProps) => {
  const { date } = props
  const events = orderBy(
    props.events,
    e => getTrackEventDate(e.eventDateTime).toDate(),
    ['desc'],
  )
  return (
    <>
      <div className="bg-fv-beer-xlight flex items-center p-2 border-fv-beer-medium border">
        <Icon
          icon="calendar-day"
          className="text-fv-gray-dark"
          transform="up-1.5"
        />
        <span className="leading-none">
          {getTrackEventDate(date).format('MM/DD/YYYY')}
        </span>
        {!props.hasAmbiguousTimes && (
          <span className="ml-2 text-sm italic ">
            (all times in {timeZoneAbbreviation})
          </span>
        )}
      </div>
      <div className="relative pt-2">
        <div className="top-0 bottom-[calc(1rem)] w-px bg-fv-gray-500 absolute left-[calc(1.15rem-1px)]" />
        <List className="ml-[1.15rem] relative">
          {!!props.stopsAway && (
            <StopsAwayListItem stopsAway={props.stopsAway} />
          )}
          {!!props.interline && (
            <InterlineListItem interlineDetails={props.interline} />
          )}
          {events.map((e, ix) => (
            <TrackEventListItem key={ix} item={e} />
          ))}
        </List>
      </div>
    </>
  )
}
